import { defineStore, storeToRefs } from 'pinia'
import { Ref, ref, computed } from 'vue'
import { validate as isValidUuid } from 'uuid'
import {
  type HotkeyKeyValuePair,
  type ManagedSurveyQueryResponse,
  type Survey,
  type SurveyAction,
  type SurveyActionResponse,
  type SurveyAnswer,
  type SurveyResponse,
  SurveyOptionsSortBy,
  ScreenName,
  SurveyAndActionResponse
} from '@/types'
import gtmClient from '@/utils/gtmClient'
import { sentryCapture } from '@/utils/sentry'
import { useLazyQuery, useMutation } from '@vue/apollo-composable'
import { track, Events, BrazeEvents } from '@outputinc/o-analytics'

import {
  fetchManagedSurveys as fetchManagedSurveysQuery,
  fetchCustomerSurveyResponses as fetchCustomerSurveyResponsesQuery,
  postCustomerSurveyResponse as postCustomerSurveyResponseMutation
} from '@/queries'
import { useBrazeStore } from '@/store/BrazeStore'
import { useCheckoutStore } from '@/store/CheckoutStore'
import { useModalStore } from '@/store/ModalStore'
import { usePlanStore } from '@/store/PlanStore'
import { useProductStore } from '@/store/ProductStore'
import { useUserStore } from '@/store/UserStore'
import { shuffleHotKeys } from '@/utils/shuffleHotKeys'
import { useScreenStore } from '@/store/ScreenStore'

export const useSurveyStore = defineStore('SurveyStore', () => {
  const productStore = useProductStore()
  const checkoutStore = useCheckoutStore()
  const userStore = useUserStore()
  const planStore = usePlanStore()
  const { trigger } = useModalStore()
  const { addScreens } = useScreenStore()
  const { trackEmailUser } = useBrazeStore()

  const { surveyResponses } = storeToRefs(checkoutStore)
  const { outputUid, surveysHasAnswered } = storeToRefs(userStore)
  const { selectedPlanProduct } = storeToRefs(planStore)
  const { checkoutSurveyGroup, productId } = storeToRefs(productStore)
  const surveys: Ref<Array<Survey>> = ref([])
  const surveyId: Ref<string> = ref('')

  const setSurveyId = (id: string) => {
    surveyId.value = id
  }

  const sortAnswers = (answers: Array<HotkeyKeyValuePair>, sortBy?: string): Array<HotkeyKeyValuePair> => {
    switch (sortBy) {
      case SurveyOptionsSortBy.Random:
        return shuffleHotKeys([...answers])
      case SurveyOptionsSortBy.Ranked:
      default:
        return answers
    }
  }

  const reduceSurveyResponse = (res: SurveyAndActionResponse) => {
    const surveysAndAnswers = res?.surveys
      .filter((survey): survey is ManagedSurveyQueryResponse => survey != null && survey.content != null)
      .map(survey => {
        const {
          uuid,
          content: { answers, question, surveyId: id, shortProperty: property, sortBy }
        } = survey
        const actions = res.actions?.filter(action => action.surveyReference === uuid)

        const unshuffledAnswers = answers.map(({ label, value }) => {
          return {
            label,
            value
          }
        })

        const orderedAnswers = sortAnswers(unshuffledAnswers, sortBy)

        return {
          id,
          property,
          answers: orderedAnswers,
          question,
          ...(actions && { actions })
        }
      })

    return surveysAndAnswers
  }

  const {
    onResult: onFetchSurveysResult,
    load: fetchManagedSurveys,
    loading: surveysLoading,
    onError: onFetchSurveysError
  } = useLazyQuery(fetchManagedSurveysQuery)

  const fetchSurveys = () => {
    const version = import.meta.env.VITE_OUTPUT_ENVIRONMENT !== 'production' ? 'draft' : 'published'

    const isUuid = isValidUuid(checkoutSurveyGroup.value)

    return fetchManagedSurveys(
      fetchManagedSurveysQuery,
      {
        storyId: checkoutSurveyGroup.value,
        version,
        relations: 'checkout-survey-group.surveys,checkout-survey-action.modalContent',
        ...(isUuid && { params: '&find_by=uuid' })
      },
      {
        errorPolicy: 'ignore',
        fetchPolicy: 'cache-first',
        context: {
          fetchOptions: {
            method: 'GET'
          }
        }
      }
    )
  }

  onFetchSurveysResult(result => {
    if (!result?.data) {
      return
    }

    const { fetchManagedStoryContent } = result.data
    const { rels: surveysRelations, story } = fetchManagedStoryContent

    if (!surveysRelations || !story?.content) {
      return
    }

    const {
      content: { surveys: surveysReferences, actions: actionsResponse }
    } = story

    const surveysResponse = surveysReferences
      .map((survey: string) => surveysRelations.find((surveyResponse: ManagedSurveyQueryResponse) => surveyResponse.uuid === survey))
      .filter(Boolean)

    const actions = actionsResponse?.map((action: SurveyActionResponse) => {
      const { modalContent, surveyGroup: surveyReference, surveyOptionValue: option } = action

      const { content, slug: modalName } = surveysRelations.find((reference: ManagedSurveyQueryResponse) => reference.uuid === modalContent)

      return {
        content,
        surveyReference,
        option,
        modalName
      }
    })

    const surveysAndActionsResponse = {
      surveys: surveysResponse,
      actions
    }

    const surveyQuestionsAndAnswers = reduceSurveyResponse(surveysAndActionsResponse)

    if (surveyQuestionsAndAnswers?.length > 0) {
      surveys.value = surveyQuestionsAndAnswers

      const surveysToDisplay = surveys.value?.filter(survey => !surveysHasAnswered.value.includes(survey.id))

      const surveyScreens = surveysToDisplay?.map((survey: Survey) => {
        const { property: surveyId } = survey
        return {
          id: ScreenName.Survey,
          name: '',
          params: {
            surveyId
          }
        }
      })

      if (surveyScreens) {
        addScreens(surveyScreens)
      }
    }
  })

  // Map any Survey Responses (selected) to array for store access
  const responses = computed(() =>
    Object.keys(surveyResponses.value).map(responseId => {
      return { id: responseId, answer: surveyResponses.value[responseId] }
    })
  )

  const getSurveyById = computed(() => (key: string) => surveys.value.find((survey: Survey) => survey.id === key))
  const getSurveyByProperty = computed(() => (key: string) => surveys.value.find((survey: Survey) => survey.property === key))
  const currentSurvey = computed(() => surveys.value.find((survey: Survey) => survey.property === surveyId.value))
  const currentSurveyActions = computed(() => currentSurvey.value?.actions)
  const surveyHasActions = computed(() => {
    const actions = currentSurvey.value?.actions
    return actions && actions.length > 0
  })

  const handleAction = (option: SurveyAnswer): boolean => {
    const surveyAction = currentSurveyActions.value?.find((action: SurveyAction) => action.option === option.value)

    if (!surveyAction) {
      return false
    }

    const { content, modalName } = surveyAction
    const { body, ctaText, ctaLink, heading: title, image, linkHostname } = content
    const modal = { title, image: image.filename, body, ctaText, ctaLink, modalName, linkHostname }
    trigger(modal)
    return true
  }

  const handleSurveyResponse = (response: SurveyResponse) => {
    const { surveyResponses } = storeToRefs(checkoutStore)
    const { setCheckoutProperty } = checkoutStore
    const { setCustomerSurveyResponses } = useUserStore()
    const { track: gtmTrackEvent, Events: GtmEvents } = gtmClient
    const { id: surveyId, answer, property } = response

    const updatedResponses = {
      ...surveyResponses.value,
      [surveyId]: answer
    }

    trackEmailUser({
      externalId: outputUid.value,
      name: BrazeEvents.CHECKOUT_SUBSCRIPTION_QUESTION_SELECT,
      time: new Date().toISOString(),
      properties: {
        key: property,
        value: answer,
        products: currentSurveyProduct.value
      }
    })

    gtmTrackEvent(GtmEvents.CHECKOUT_SUBSCRIPTION_QUESTION_SELECT, { [property]: answer })

    track({
      event_type: Events.CHECKOUT_SUBSCRIPTION_QUESTION_SELECT,
      event_properties: {
        products: [currentSurveyProduct.value],
        surveyId,
        surveyResponse: answer
      },
      user_properties: { [surveyId]: answer }
    })

    const surveys = {
      [surveyId]: {
        surveyId,
        responseSlug: answer,
        responseTimestamp: new Date().toISOString()
      }
    }

    postSurveyResponse({
      surveys
    })

    // updateUserStore
    setCustomerSurveyResponses([
      {
        [surveyId]: answer
      }
    ])

    // update responses in the checkout store
    setCheckoutProperty({ surveyResponses: updatedResponses })
  }

  const { load: fetchSurveyResponses, onResult: onSurveyResponsesResult } = useLazyQuery(fetchCustomerSurveyResponsesQuery)

  onSurveyResponsesResult(result => {
    if (!result.data) {
      return
    }
    const { setCustomerSurveyResponses } = useUserStore()
    const { fetchCustomerSurveyResponses } = result.data

    if (fetchCustomerSurveyResponses.surveys) {
      const responses = Object.keys(fetchCustomerSurveyResponses.surveys).map(key => {
        const { surveyId, responseSlug } = fetchCustomerSurveyResponses.surveys[key]

        return {
          [surveyId]: responseSlug
        }
      })

      setCustomerSurveyResponses(responses)
    }
  })

  const { mutate: postSurveyResponse, onError: onPostSurveyResponseError } = useMutation(postCustomerSurveyResponseMutation)

  onFetchSurveysError(err => sentryCapture(err))
  onPostSurveyResponseError(err => sentryCapture(err))

  const loading = computed(() => surveysLoading.value)
  const currentSurveyProduct = computed(() => selectedPlanProduct.value || productId.value)

  return {
    currentSurvey,
    currentSurveyProduct,
    fetchSurveyResponses,
    fetchSurveys,
    getSurveyById,
    getSurveyByProperty,
    handleAction,
    handleSurveyResponse,
    loading,
    postSurveyResponse,
    responses,
    setSurveyId,
    surveyHasActions,
    surveyId,
    surveys
  }
})
